import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  RecordingBar,
  VoiceInputIdle,
  appendTranscriptToDraft,
  focusDraftEnd,
  useVoiceInput,
  type VoiceInputErrorKind
} from '@bliks-io/ui';
import { isForbiddenError } from '@/api/errors';
import { useTranscribeAudio } from '@/api/mutations/useTranscribeAudio';

const ERROR_MESSAGE_KEYS: Record<VoiceInputErrorKind, string> = {
  'mic-denied': 'voiceInput.microphoneDenied',
  'record-failed': 'voiceInput.recordingFailed',
  'transcribe-failed': 'voiceInput.transcriptionFailed'
};

interface UseVoiceInputSlotOptions {
  /** State setter or callback that receives the new input value */
  onChange: (value: string) => void;
  /** Ref to the textarea — used to focus + place cursor after transcription */
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  /** Current input value (read via ref internally to avoid stale closures) */
  value: string;
  disabled?: boolean;
}

/**
 * Wires `@bliks-io/ui`'s voice input to this app's transcription endpoint and
 * translations, and returns ready-to-use `leftSlot` / `leftSlotExpanded` props
 * for ChatInput.
 *
 * Handles transcription text appending and cursor placement internally,
 * so consumers don't need to duplicate that logic.
 */
export function useVoiceInputSlot({
  onChange,
  textareaRef,
  value,
  disabled
}: UseVoiceInputSlotOptions) {
  const { t } = useTranslation();
  const { mutateAsync: transcribeAudio } = useTranscribeAudio();

  const valueRef = useRef(value);
  valueRef.current = value;

  const handleTranscription = useCallback(
    (text: string) => {
      onChange(appendTranscriptToDraft(valueRef.current, text));
      focusDraftEnd(textareaRef);
    },
    [onChange, textareaRef]
  );

  // The HTTP client already toasted a 403; the ui hook treats an empty
  // transcript as a silent no-op, so it does not add a second toast.
  const transcribe = useCallback(
    async (blob: Blob) => {
      try {
        return (await transcribeAudio(blob)).text;
      } catch (error) {
        if (isForbiddenError(error)) return '';
        throw error;
      }
    },
    [transcribeAudio]
  );

  const voice = useVoiceInput({
    transcribe,
    onTranscription: handleTranscription,
    onError: (kind) => toast.error(t(ERROR_MESSAGE_KEYS[kind]))
  });

  const leftSlot = voice.isActive ? (
    <RecordingBar
      elapsed={voice.elapsed}
      isTranscribing={voice.isTranscribing}
      onStop={voice.stopAndTranscribe}
      onCancel={voice.cancelRecording}
      stopLabel={t('voiceInput.stopRecording')}
      cancelLabel={t('voiceInput.cancelRecording')}
      transcribingLabel={t('voiceInput.transcribing')}
    />
  ) : (
    <VoiceInputIdle
      onStart={voice.startRecording}
      disabled={disabled}
      label={t('voiceInput.startRecording')}
    />
  );

  return { leftSlot, leftSlotExpanded: voice.isActive };
}
